import { z } from 'zod';
import { parseDate } from '../utils/date';

/** Only addresses and counts; cell contents never enter the activity log. */
export const activitySelectionSchema = z.object({
  rowIds: z.array(z.string().max(64)).max(100).default([]),
  fields: z.array(z.string().max(100)).max(200),
  sheetCode: z.string().max(64).optional(),
  rowCount: z.number().int().min(0).max(100_000),
  cellCount: z.number().int().min(0).max(20_000_000),
});

export const activityLogSchema = activitySelectionSchema.extend({
  kind: z.enum(['selection', 'copy']),
  outcome: z.enum(['attempted', 'blocked']).optional(),
});

export type ActivitySelection = z.infer<typeof activitySelectionSchema>;
export type ActivityLog = z.infer<typeof activityLogSchema>;

export const activityContextSchema = z.object({
  path: z.string().max(250).regex(/^\/(?!\/)/),
  label: z.string().max(100),
  sheetCode: z.string().max(100).optional(),
});
export type ActivityContext = z.infer<typeof activityContextSchema>;


const activityDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Ожидается дата в формате гггг-мм-дд')
  .refine((value) => parseDate(value) === value, 'Такой даты не существует');

const activityTimeZoneSchema = z.string().min(1).max(64).refine((timeZone) => {
  // Intl также умеет смещения вроде +03:00; API принимает именованные зоны IANA.
  if (!/^[A-Za-z][A-Za-z0-9._+\-/]*$/.test(timeZone)) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
}, 'Неизвестный часовой пояс');

/** Пользовательская лента; переданный actorId никогда не меняет scope=me. */
export const activityQuerySchema = z.object({
  scope: z.enum(['me', 'all']).default('me'),
  kind: z.enum(['all', 'changes', 'actions']).default('all'),
  from: activityDateSchema.optional(),
  to: activityDateSchema.optional(),
  timeZone: activityTimeZoneSchema.default('UTC'),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).max(2_147_483_647).default(0),
}).refine(({ from, to }) => !from || !to || from <= to, {
  path: ['from'], message: 'Начало периода не может быть позже окончания',
});
export type ActivityQuery = z.infer<typeof activityQuerySchema>;

export const activityItemSchema = z.object({
  id: z.string(),
  kind: z.enum(['change', 'action']),
  createdAt: z.string(),
  actorId: z.string().nullable(),
  actorName: z.string().nullable(),
  title: z.string(),
  description: z.string().nullable(),
  targetLabel: z.string().nullable(),
  targetUrl: z.string().nullable(),
  changes: z.array(z.object({
    label: z.string(),
    before: z.string().nullable(),
    after: z.string().nullable(),
    redacted: z.boolean().optional(),
  })),
});
export type ActivityItem = z.infer<typeof activityItemSchema>;
export const activityResponseSchema = z.object({
  items: z.array(activityItemSchema),
  total: z.number().int().nonnegative(),
  hasMore: z.boolean(),
});
export type ActivityResponse = z.infer<typeof activityResponseSchema>;
